import { z } from "zod";

/**
 * What a landing page has to say about a store beyond its name — the things
 * that currently live in app code because there was nowhere to read them from.
 *
 * The two bespoke landings between them render: a tagline
 * (`bestkebab/src/components/Hero.tsx`), an address block and a phone number
 * (`bestkebab/src/lib/shop.ts`, `piconi/src/components/Footer.tsx`), a link to
 * the shop on a map and its social accounts (Piconi's footer), a weekly hours
 * table, an "open now / closed" badge driven by those same hours, and a logo.
 * That list is this schema. Nothing here is speculative: every field is
 * something one of those two pages already draws.
 *
 * Every field is optional with a default, so `StoreProfileSchema.parse({})` is
 * a complete, renderable profile for a store that has filled nothing in. That
 * is why `StoreProfile` — the parsed *output* type — has no optional members:
 * a page reads `profile.phone` and checks it against `null`, never against
 * `undefined` as well.
 */

/**
 * A URL a page will put in an `href` or a `src`.
 *
 * The scheme check is not redundant with `.url()`. Zod's `.url()` delegates to
 * the WHATWG `URL` parser, which happily accepts `javascript:alert(1)` — a
 * valid URL, and a script that runs the moment a customer clicks the store's
 * "find us" link or a browser resolves its logo. A store's own admin is the
 * only person who can write these, but "the attacker needs a tenant_admin
 * account" is not the same as "this is safe", and a landing page renders them
 * to the public.
 */
export const PublicUrlSchema = z
  .string()
  .url()
  .max(2048)
  .refine(
    (value) => value.startsWith("https://") || value.startsWith("http://"),
    "must be an http(s) URL",
  );

/** 24-hour clock time, `HH:MM`. */
export const TimeOfDaySchema = z
  .string()
  .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, "must be a 24-hour time, HH:MM");

/**
 * One continuous serving session.
 *
 * `to` earlier than `from` means the session runs past midnight — Best Kebab's
 * Saturday is `16:00`–`00:30`. The interval is half-open, `from` inclusive and
 * `to` exclusive: a shop that closes at 22:00 is not taking orders at 22:00,
 * and back-to-back windows (`12:00`–`15:00`, `15:00`–`23:00`) do not both
 * claim 15:00.
 *
 * `from === to` is rejected rather than given a meaning. Read one way it is a
 * zero-length window, read the other it wraps the whole way round to 24 hours
 * open — and the two existing implementations genuinely disagree about which
 * (`bestkebab/src/lib/hours.ts` uses `end < start`, Piconi's footer uses
 * `closes <= opens`). Refusing to store the ambiguity is cheaper than picking
 * a side and hoping every future reader picks the same one.
 */
export const OpeningWindowSchema = z
  .object({
    from: TimeOfDaySchema,
    to: TimeOfDaySchema,
  })
  .refine(
    (w) => w.from !== w.to,
    "from and to must differ: use two windows, or omit the day, rather than a zero-length one",
  );
export type OpeningWindow = z.infer<typeof OpeningWindowSchema>;

/** A day index as `Date.prototype.getDay()` reports it: 0 is Sunday. */
export const DayOfWeekSchema = z.number().int().min(0).max(6);

/**
 * A day's sessions, carried as a list rather than as a single window.
 *
 * Neither live store splits its day today, so one window would cover both. The
 * list is still the right shape, because the asymmetry runs the wrong way:
 * this is stored data. A landing page that renders `windows[0]` and one that
 * renders all of them are the same afternoon's work, but widening a stored
 * `window` into `windows` later means a migration plus a backfill of every
 * store's row. And a restaurant that shuts between lunch and dinner service is
 * not a hypothetical — it is the ordinary case this platform is being sold to.
 *
 * An empty list means closed all day, which is also what omitting the day
 * entirely means. The two are equivalent on purpose: a form that renders seven
 * rows and a form that renders only the open ones both produce valid data.
 */
export const DayHoursSchema = z.object({
  day: DayOfWeekSchema,
  windows: z.array(OpeningWindowSchema).max(3).default([]),
});
export type DayHours = z.infer<typeof DayHoursSchema>;

/**
 * The week, keyed explicitly by day number rather than by position.
 *
 * `bestkebab/src/lib/hours.ts` uses a seven-element array whose index *is* the
 * day, and says so in a comment warning that the two must not drift apart —
 * which is exactly the kind of invariant that does drift once the data is
 * round-tripping through JSON and an edit form. Carrying `day` on each entry
 * costs one field and makes a reordered or partial list harmless.
 */
export const OpeningHoursSchema = z
  .array(DayHoursSchema)
  .max(7)
  .superRefine((days, ctx) => {
    const seen = new Set<number>();
    for (const entry of days) {
      if (seen.has(entry.day)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `day ${entry.day} appears more than once`,
        });
      }
      seen.add(entry.day);
    }
  })
  .default([]);
export type OpeningHours = z.infer<typeof OpeningHoursSchema>;

/**
 * An IANA time zone name.
 *
 * Validated by asking the platform's own zone database rather than by pattern,
 * because `Africa/Algiers` and `Africa/Algeria` are equally well-formed and
 * only one of them exists. `Intl.DateTimeFormat` throws `RangeError` on an
 * unknown zone in both Node and every browser this ships to.
 */
export const TimeZoneSchema = z
  .string()
  .min(1)
  .max(64)
  .refine(isValidTimeZone, "must be an IANA time zone, e.g. Africa/Algiers");

function isValidTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

export const SocialLinksSchema = z
  .object({
    facebook: PublicUrlSchema.nullable().default(null),
    instagram: PublicUrlSchema.nullable().default(null),
    tiktok: PublicUrlSchema.nullable().default(null),
  })
  .default({});
export type SocialLinks = z.infer<typeof SocialLinksSchema>;

const DAY_MS = 86_400_000;

function dayNumber(date: string): number {
  return Date.parse(`${date}T00:00:00Z`) / DAY_MS;
}

function shiftDate(date: string, days: number): string {
  return new Date((dayNumber(date) + days) * DAY_MS).toISOString().slice(0, 10);
}

function isCalendarDate(value: string): boolean {
  const time = Date.parse(`${value}T00:00:00Z`);
  return !Number.isNaN(time) && new Date(time).toISOString().slice(0, 10) === value;
}

export const CalendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "must be a date, YYYY-MM-DD")
  .refine(isCalendarDate, "must be a real calendar date");
export type CalendarDate = z.infer<typeof CalendarDateSchema>;

export const MAX_HOUR_EXCEPTIONS = 50;
export const MAX_HOUR_EXCEPTION_DAYS = 92;
export const MAX_HOUR_EXCEPTION_LABEL = 40;

export const HourExceptionSchema = z.object({
  from: CalendarDateSchema,
  to: CalendarDateSchema,
  label: z
    .string()
    .trim()
    .max(MAX_HOUR_EXCEPTION_LABEL)
    .nullable()
    .default(null)
    .transform((value) => (value === "" ? null : value)),
  windows: z.array(OpeningWindowSchema).max(3).default([]),
});
export type HourException = z.infer<typeof HourExceptionSchema>;

export type HourExceptionRange = Pick<HourException, "from" | "to">;

export interface HourExceptionConflict {
  kind: "same" | "overlap";
  with: HourException;
}

export function exceptionConflict(
  list: readonly HourException[],
  candidate: HourExceptionRange,
): HourExceptionConflict | null {
  const same = list.find((entry) => entry.from === candidate.from && entry.to === candidate.to);
  if (same) return { kind: "same", with: same };

  const overlapping = list.find((entry) => {
    const intersects = entry.from <= candidate.to && candidate.from <= entry.to;
    const entryContains = entry.from <= candidate.from && candidate.to <= entry.to;
    const candidateContains = candidate.from <= entry.from && entry.to <= candidate.to;
    return intersects && !entryContains && !candidateContains;
  });
  return overlapping ? { kind: "overlap", with: overlapping } : null;
}

export function exceptionSpanDays(range: HourExceptionRange): number {
  return dayNumber(range.to) - dayNumber(range.from) + 1;
}

export const HourExceptionListSchema = z
  .array(HourExceptionSchema)
  .max(MAX_HOUR_EXCEPTIONS)
  .superRefine((list, ctx) => {
    const wellFormed: HourException[] = [];
    list.forEach((entry, index) => {
      if (entry.to < entry.from) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, "to"],
          message: "the last day cannot be before the first day",
        });
        return;
      }
      if (exceptionSpanDays(entry) > MAX_HOUR_EXCEPTION_DAYS) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, "to"],
          message: `an exception can cover at most ${MAX_HOUR_EXCEPTION_DAYS} days`,
        });
        return;
      }
      const conflict = exceptionConflict(wellFormed, entry);
      if (conflict) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, "from"],
          message:
            conflict.kind === "same"
              ? `another exception already covers ${entry.from} to ${entry.to}`
              : `${entry.from} to ${entry.to} partly overlaps ${conflict.with.from} to ${conflict.with.to}: keep exceptions apart or fully nested`,
        });
      }
      wellFormed.push(entry);
    });
  });

export const HourExceptionsSchema = HourExceptionListSchema.default([]);

export const StoreProfileSchema = z.object({
  /** The line under the name: Best Kebab's "A short menu, cooked over coals." */
  tagline: z.string().max(200).nullable().default(null),
  /**
   * The address as it is printed, one entry per line. A footer sets these on
   * their own lines and does not re-wrap them, so where the breaks fall is
   * content and belongs to the store, not to the layout.
   */
  addressLines: z.array(z.string().min(1).max(120)).max(4).default([]),
  /** Where "find us" points. Piconi's footer links its address to a map. */
  mapUrl: PublicUrlSchema.nullable().default(null),
  /** As a human dials it locally: `0549 85 95 52`. */
  phone: z.string().max(40).nullable().default(null),
  /**
   * The same number in E.164, for the `tel:` href. Kept separate rather than
   * derived, because the local spelling is not mechanically convertible — both
   * landings already carry the pair for this reason.
   */
  phoneE164: z
    .string()
    .regex(/^\+[1-9]\d{1,14}$/, "must be E.164, e.g. +213549859552")
    .nullable()
    .default(null),
  /**
   * The store's mark. Any http(s) URL, which includes the MinIO bucket that
   * `POST /uploads/menu-image` already writes to — so a logo is an upload plus
   * a profile update, with no new storage path to provision.
   */
  logoUrl: PublicUrlSchema.nullable().default(null),
  /**
   * The zone `openingHours` is written in.
   *
   * Without it "are you open right now" is answered in the *visitor's* zone,
   * which is what both landings do today and is wrong for anyone abroad —
   * a customer in Paris ordering for family in Oran is not a corner case for
   * this platform, it is a use case. Defaults to UTC: an unset zone should be
   * conspicuous, not silently the developer's own.
   */
  timezone: TimeZoneSchema.default("UTC"),
  openingHours: OpeningHoursSchema,
  hourExceptions: HourExceptionsSchema,
  social: SocialLinksSchema,
});
export type StoreProfile = z.infer<typeof StoreProfileSchema>;

const MINUTES_PER_DAY = 1440;

/** `HH:MM` to minutes since local midnight. */
function minutesOf(time: string): number {
  return Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
}

const WEEKDAY_INDEX: Readonly<Record<string, number>> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

interface LocalClock {
  date: string;
  /** As `Date.prototype.getDay()` reports it, but in the store's zone. */
  day: number;
  /** Minutes since midnight in the store's zone. */
  minutes: number;
}

const clockFormats = new Map<string, Intl.DateTimeFormat>();

function clockFormat(timeZone: string): Intl.DateTimeFormat {
  let format = clockFormats.get(timeZone);
  if (!format) {
    format = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    clockFormats.set(timeZone, format);
  }
  return format;
}

/**
 * What day and time it is *at the shop*.
 *
 * `hourCycle: "h23"` rather than `hour12: false`, which is specified to be
 * able to produce `"24"` for midnight and would put the shop 24 hours into a
 * day that has 1440 minutes in it.
 */
function localClock(at: Date, timeZone: string): LocalClock {
  let year = "1970";
  let month = "01";
  let dayOfMonth = "01";
  let day = 0;
  let hour = 0;
  let minute = 0;
  for (const part of clockFormat(timeZone).formatToParts(at)) {
    if (part.type === "year") year = part.value.padStart(4, "0");
    else if (part.type === "month") month = part.value;
    else if (part.type === "day") dayOfMonth = part.value;
    else if (part.type === "weekday") day = WEEKDAY_INDEX[part.value] ?? 0;
    else if (part.type === "hour") hour = Number(part.value);
    else if (part.type === "minute") minute = Number(part.value);
  }
  return { date: `${year}-${month}-${dayOfMonth}`, day, minutes: hour * 60 + minute };
}

export type HoursProfile = Pick<StoreProfile, "openingHours" | "timezone"> &
  Partial<Pick<StoreProfile, "hourExceptions">>;

export type ExceptionsProfile = Pick<StoreProfile, "timezone"> &
  Partial<Pick<StoreProfile, "hourExceptions">>;

export function exceptionOn(
  profile: Partial<Pick<StoreProfile, "hourExceptions">>,
  date: string,
): HourException | undefined {
  let innermost: HourException | undefined;
  for (const entry of profile.hourExceptions ?? []) {
    if (entry.from > date || entry.to < date) continue;
    if (!innermost || exceptionSpanDays(entry) < exceptionSpanDays(innermost)) innermost = entry;
  }
  return innermost;
}

function weeklyWindowsOn(hours: OpeningHours, day: number): readonly OpeningWindow[] {
  return hours.find((entry) => entry.day === day)?.windows ?? [];
}

function windowsOn(profile: HoursProfile, date: string, day: number): readonly OpeningWindow[] {
  return exceptionOn(profile, date)?.windows ?? weeklyWindowsOn(profile.openingHours, day);
}

/**
 * Whether the store is serving at `at`.
 *
 * Lives here, next to the shape, rather than in each landing app, because the
 * hard half of this question is a property of the data and has already been
 * got wrong once per app: a session that began yesterday and has not closed
 * yet. At 00:15 on a Sunday, Best Kebab is open — on *Saturday's* 16:00–00:30
 * window — and a same-day check reports it shut. Both existing apps carry
 * their own copy of this logic; the shared engine gets one that is tested.
 *
 * Takes the profile rather than loose arguments so the hours and the zone they
 * are written in cannot be passed separately and drift.
 */
export function isOpenAt(profile: HoursProfile, at: Date): boolean {
  const { date, day, minutes } = localClock(at, profile.timezone);

  for (const w of windowsOn(profile, date, day)) {
    const start = minutesOf(w.from);
    const end = minutesOf(w.to);
    const close = end < start ? end + MINUTES_PER_DAY : end;
    if (minutes >= start && minutes < close) return true;
  }

  for (const w of windowsOn(profile, shiftDate(date, -1), (day + 6) % 7)) {
    const start = minutesOf(w.from);
    const end = minutesOf(w.to);
    if (end < start && minutes < end) return true;
  }

  return false;
}

const MINUTE_MS = 60_000;
const MAX_BOUNDARY_STEPS = 1000;

function boundariesOn(profile: HoursProfile, date: string, day: number): number[] {
  const today = windowsOn(profile, date, day).flatMap((w) => {
    const start = minutesOf(w.from);
    const end = minutesOf(w.to);
    return end < start ? [start] : [start, end];
  });
  const carriedOver = windowsOn(profile, shiftDate(date, -1), (day + 6) % 7)
    .filter((w) => minutesOf(w.to) < minutesOf(w.from))
    .map((w) => minutesOf(w.to));
  return [...today, ...carriedOver, MINUTES_PER_DAY];
}

function localMinuteKey(at: Date, timeZone: string): number {
  const { date, minutes } = localClock(at, timeZone);
  return dayNumber(date) * MINUTES_PER_DAY + minutes;
}

export function offsetMsAt(instantMs: number, timeZone: string): number {
  return localMinuteKey(new Date(instantMs), timeZone) * MINUTE_MS - instantMs;
}

function firstInstantAtOrPast(targetKey: number, timeZone: string): number {
  const naiveMs = targetKey * MINUTE_MS;
  const candidates = [
    naiveMs - offsetMsAt(naiveMs - DAY_MS, timeZone),
    naiveMs - offsetMsAt(naiveMs + DAY_MS, timeZone),
  ].sort((a, b) => a - b);

  const exact = candidates.find(
    (candidate) => localMinuteKey(new Date(candidate), timeZone) === targetKey,
  );
  if (exact !== undefined) return exact;

  let before = candidates[0] ?? naiveMs;
  let after = candidates[1] ?? naiveMs;
  while (after - before > MINUTE_MS) {
    const middle = before + Math.floor((after - before) / (2 * MINUTE_MS)) * MINUTE_MS;
    if (localMinuteKey(new Date(middle), timeZone) >= targetKey) after = middle;
    else before = middle;
  }
  return after;
}

function nextChangeAfter(profile: HoursProfile, at: Date, toOpen: boolean): Date | null {
  let cursor = new Date(Math.floor(at.getTime() / MINUTE_MS) * MINUTE_MS);
  let wasOpen = isOpenAt(profile, cursor);
  for (let step = 0; step < MAX_BOUNDARY_STEPS; step++) {
    const { date, day, minutes } = localClock(cursor, profile.timezone);
    const next = Math.min(
      ...boundariesOn(profile, date, day).filter((boundary) => boundary > minutes),
    );
    const boundaryMs = firstInstantAtOrPast(
      dayNumber(date) * MINUTES_PER_DAY + next,
      profile.timezone,
    );
    const stillAhead = cursor.getTime() + (next - minutes) * MINUTE_MS;
    cursor = new Date(boundaryMs > cursor.getTime() ? boundaryMs : stillAhead);
    const open = isOpenAt(profile, cursor);
    if (open === toOpen && open !== wasOpen) return cursor;
    wasOpen = open;
  }
  return null;
}

export function hasAnyWindow(profile: HoursProfile, at: Date = new Date()): boolean {
  return (
    profile.openingHours.some((entry) => entry.windows.length > 0) ||
    currentExceptions(profile, at).some((entry) => entry.windows.length > 0)
  );
}

export function nextOpeningAfter(profile: HoursProfile, at: Date): Date | null {
  if (!hasAnyWindow(profile, at)) return null;
  return nextChangeAfter(profile, at, true);
}

export function nextClosingAfter(profile: HoursProfile, at: Date): Date | null {
  if (!isOpenAt(profile, at)) return null;
  return nextChangeAfter(profile, at, false);
}

export type OpenStatus =
  | { state: "paused"; until: Date | null }
  | { state: "open"; closesAt: Date | null }
  | { state: "closed"; opensAt: Date | null };

export function openStatusAt(
  profile: HoursProfile,
  ordering: { accepting: boolean; pausedUntil: string | null },
  at: Date,
): OpenStatus {
  if (!ordering.accepting) {
    const until = ordering.pausedUntil === null ? null : new Date(ordering.pausedUntil);
    if (until === null || until.getTime() > at.getTime()) return { state: "paused", until };
  }
  if (isOpenAt(profile, at)) return { state: "open", closesAt: nextClosingAfter(profile, at) };
  return { state: "closed", opensAt: nextOpeningAfter(profile, at) };
}

export interface DayAhead {
  date: string;
  day: number;
  windows: readonly OpeningWindow[];
  label: string | null;
  isException: boolean;
}

export function weekAhead(profile: HoursProfile, at: Date): DayAhead[] {
  const today = localClock(at, profile.timezone);
  return Array.from({ length: 7 }, (_, offset) => {
    const date = shiftDate(today.date, offset);
    const day = (today.day + offset) % 7;
    const exception = exceptionOn(profile, date);
    return {
      date,
      day,
      windows: exception?.windows ?? weeklyWindowsOn(profile.openingHours, day),
      label: exception?.label ?? null,
      isException: exception !== undefined,
    };
  });
}

function byFirstDay(a: HourException, b: HourException): number {
  if (a.from !== b.from) return a.from < b.from ? -1 : 1;
  if (a.to !== b.to) return a.to < b.to ? -1 : 1;
  return 0;
}

function exceptionsEndingOnOrAfter(profile: ExceptionsProfile, date: string): HourException[] {
  return (profile.hourExceptions ?? []).filter((entry) => entry.to >= date).sort(byFirstDay);
}

export function upcomingExceptions(profile: ExceptionsProfile, at: Date): HourException[] {
  return exceptionsEndingOnOrAfter(profile, localClock(at, profile.timezone).date);
}

export function currentExceptions(profile: ExceptionsProfile, at: Date): HourException[] {
  return exceptionsEndingOnOrAfter(profile, shiftDate(localClock(at, profile.timezone).date, -1));
}

export function shopDay(at: Date, timeZone: string): string {
  return localClock(at, timeZone).date;
}

export function shopWeekday(at: Date, timeZone: string): number {
  return localClock(at, timeZone).day;
}

export const FAR_AHEAD_DAYS = 6;

export function resumeLabel(
  pausedUntil: string,
  timeZone: string,
  now: Date = new Date(),
  locale = "en-GB",
): string {
  const at = new Date(pausedUntil);
  if (Number.isNaN(at.getTime())) return "";

  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(at);
  const targetDay = shopDay(at, timeZone);
  const today = shopDay(now, timeZone);
  if (targetDay === today) return time;

  const weekday = new Intl.DateTimeFormat(locale, { timeZone, weekday: "short" }).format(at);
  if (dayNumber(targetDay) - dayNumber(today) < FAR_AHEAD_DAYS) return `${weekday} ${time}`;

  const dayOfMonth = new Intl.DateTimeFormat(locale, { timeZone, day: "numeric" }).format(at);
  const month = new Intl.DateTimeFormat(locale, { timeZone, month: "short" }).format(at);
  return `${weekday} ${dayOfMonth} ${month} ${time}`;
}
